import React, { PureComponent, CSSProperties } from 'react';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { Select, Spin } from 'antd';
import _ from 'lodash';
import { SearchOutlined, LoadingOutlined } from '@ant-design/icons';
import i18n from 'i18next';

const Option = Select.Option;

export interface IsRef {
  id: string;
  name: any;
}

abstract class PickerBase<P extends IsRef, T> extends PureComponent<IPickerBaseProps<P> & T, IPickerBaseState<P>> {
  private onSearch$: Subject<string>;
  private subscription: Subscription;

  state: IPickerBaseState<P> = {
    data: [],
    value: {} as P | P[],
    isLoading: false,
    lastSearchQuery: '',
    hasError: false,
  };

  constructor(props: any) {
    super(props);
    this.getOptions = _.memoize(this.getOptions);
  }

  async componentDidMount() {
    this.onSearch$ = new Subject<string>();
    const allOptions = await this.queryItems('');
    this.setState({ data: allOptions });
    let singleDefaultValue = null;
    if (allOptions && allOptions.length === 1 && !this.state.value && this.props.defaultIfSingle) {
      singleDefaultValue = allOptions[0];
      this.handleChange(singleDefaultValue.id);
    }

    this.subscription = this.onSearch$
      .pipe(
        debounceTime(500),
        switchMap((query) => this.queryItems(query)),
      )
      .subscribe((options) => this.setState({ data: options }));

    if (this.props.onRef) {
      this.props.onRef(this);
    }
  }

  abstract onQueryItems(query: string): Promise<P[]>;
  abstract getDisplayName(name: any): string;
  customRender(name: string, additionalInformation?: IsRef): string {
    return name;
  }

  protected useServerSideSearchOnly(): boolean {
    return false;
  }

  componentWillUnmount() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
  render() {
    const options = this.getOptions(this.state.data);
    let display = this.customRender(this.getDisplayName((this.state.value as P)?.name), this.state.value as P);

    let notFoundContent: React.ReactNode = null;
    if (this.state.isLoading) {
      notFoundContent = (
        <div style={{ textAlign: 'center', padding: '8px' }}>
          <Spin size="small" />
          &nbsp;{i18n.t('common.searching')}
        </div>
      );
    } else if (this.state.hasError) {
      notFoundContent = <div style={{ textAlign: 'center', padding: '8px' }}>{i18n.t('common.searchError')}</div>;
    } else if (this.useServerSideSearchOnly() && !this.state.lastSearchQuery) {
      notFoundContent = <div style={{ textAlign: 'center', padding: '8px' }}>{i18n.t('common.typeToSearch')}</div>;
    } else if (this.state.lastSearchQuery && (!options || (Array.isArray(options) && options.length === 0))) {
      notFoundContent = <div style={{ textAlign: 'center', padding: '8px' }}>{i18n.t('common.noEntries')}</div>;
    }

    return (
      <Select
        allowClear={this.props.allowClear}
        showSearch
        labelInValue
        value={
          this.state.value &&
          (this.props.mode === undefined
            ? (this.state.value as P)?.id && {
                value: (this.state.value as P)?.id,
                label: this.getStyledDisplayName(display),
              }
            : Array.isArray(this.state.value)
            ? (this.state.value as P[])?.map((v) => v.id && { value: v.id, label: this.getDisplayName(v.name) })
            : null)
        }
        placeholder={this.props.placeholder}
        style={this.props.style}
        defaultActiveFirstOption={false}
        showArrow={true}
        suffixIcon={this.state.isLoading === true ? <LoadingOutlined spin /> : <SearchOutlined />}
        optionFilterProp="children"
        onSearch={this.handleSearch}
        filterOption={
          this.useServerSideSearchOnly()
            ? false
            : (input, option) => {
                return option.title.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }
        }
        onChange={this.handleChange}
        notFoundContent={notFoundContent}
        onBlur={this.props.handleBlur}
        loading={this.state.isLoading}
        disabled={this.props.disabled}
        optionLabelProp="title"
        showAction={['focus', 'click']}
        data-automation-id={this.props.dataAutomationId || this.props.name}
        mode={this.props.mode}
      >
        {options}
      </Select>
    );
  }

  queryItems = async (query: string): Promise<P[]> => {
    this.setState({ isLoading: true, hasError: false });
    try {
      const items = await this.onQueryItems(query);
      this.setState({ isLoading: false });
      return items;
    } catch {
      this.setState({ isLoading: false, hasError: true });
      return [];
    }
  };

  preLastWordEllipsisStyle: React.CSSProperties = {
    display: 'inline-block',
    maxWidth: 'calc(100% - 65px)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'bottom',
  };

  lastWordStyle: React.CSSProperties = {
    display: 'inline-block',
    whiteSpace: 'nowrap',
    verticalAlign: 'bottom',
  };

  getStyledDisplayName = (name: any) => {
    const displayName = this.getDisplayName(name);
    const { preLastText, lastWord } = this.applyPreLastWordEllipsis(displayName);

    return (
      <>
        <span style={this.preLastWordEllipsisStyle}>{preLastText}</span>
        {lastWord ? (
          <>
            <span> </span>
            <span style={this.lastWordStyle}>({lastWord})</span>{' '}
          </>
        ) : (
          ''
        )}
      </>
    );
  };

  getOptions = (data: P[]) => {
    data = data || [];
    if (!data || data.length === 0) {
      return null;
    }
    this.ensureSelectedValueIsOption(data);
    return data.map((d) => {
      if (d !== undefined) {
        let displayName = this.customRender(this.getDisplayName(d.name), d);
        const { preLastText, lastWord } = this.applyPreLastWordEllipsis(displayName);

        return (
          <Option value={d.id} key={d.id} label={this.getStyledDisplayName(displayName)} title={displayName} data-automation-id={(this.props.dataAutomationId || this.props.name) + '-option-' + d.id}>
            <span className="ant-select-item-option-content" title={displayName}>
              {lastWord ? this.getStyledDisplayName(displayName) : <span>{displayName}</span>}
            </span>
          </Option>
        );
      }
    });
  };

  applyPreLastWordEllipsis(text: string) {
    const words = text.split(' ');

    const lastWordIsNumber = /^\d+$/.test(words[words.length - 1]);

    if (words.length > 1 && lastWordIsNumber) {
      const lastWord = words.pop();
      const preLastText = words.join(' ');
      return { preLastText, lastWord };
    } else {
      return { preLastText: text, lastWord: '' };
    }
  }

  ensureSelectedValueIsOption = (data: P[]) => {
    if (this.props.mode === undefined) {
      const selectedValue = this.props.value as P;
      if (_.isEmpty(selectedValue) || !selectedValue || _.some(this.state.data, (d) => d != undefined && d.id === selectedValue.id)) {
        return;
      }
      data.unshift(selectedValue);
    } else {
      if (Array.isArray(this.props.value)) {
        (this.props.value as P[])?.forEach((selectedValue) => {
          if (_.isEmpty(selectedValue) || !selectedValue || _.some(this.state.data, (d) => d != undefined && d.id === selectedValue.id)) {
            return;
          }
          data.unshift(selectedValue);
        });
      } else {
        return;
      }
      return;
    }
  };

  addAndSelectOption(data: P | P[]) {
    let newData = [] as P[];
    if (this.props.mode === undefined) {
      newData = [...this.state.data, data as P];
    } else {
      newData = [...this.state.data, ...(data as P[])];
    }
    this.setState({ value: data, data: newData });
    this.props.onChange(data);
  }

  handleSearch = (text: string) => {
    if (text && this.state.value && !_.isEmpty(this.state.value)) {
      this.setState({ value: {} as P, lastSearchQuery: text });
    } else {
      this.setState({ lastSearchQuery: text });
    }
    this.onSearch$.next(text);
  };

  handleChange = (selectedOption: { value: string; label: React.ReactNode } | { value: string; label: React.ReactNode }[]) => {
    if (this.props.mode == undefined) {
      const newValue = _.find(this.state.data, (d) => d.id === (selectedOption as { value: string; label: React.ReactNode }).value);
      this.setState({ value: newValue as P });
      this.props.onChange(newValue as P);
    } else {
      const newValue = (selectedOption as { value: string; label: React.ReactNode }[]).map((v) => _.find(this.state.data, (d) => d.id === v.value));
      this.setState({ value: newValue as P[] });
      this.props.onChange(newValue as P[]);
    }
  };
}

export default PickerBase;

export interface IPickerBaseProps<T> {
  allowClear?: boolean;
  value?: T | T[];
  placeholder?: string;
  onChange: (value: T | T[]) => void;
  onQueryItems?: (query: string) => Promise<T[]>;
  handleBlur?: (e: any) => void;
  onRef?: (e: any) => void;
  style?: CSSProperties;
  disabled?: boolean;
  defaultIfSingle?: boolean;
  dataAutomationId?: string;
  name: string;
  tenantId: string;
  branchId: string;
  mode?: 'multiple';
}

export interface IPickerBaseState<T> {
  data: T[];
  value: T | T[];
  isLoading: boolean;
  lastSearchQuery: string;
  hasError: boolean;
}
